import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Line } from 'react-chartjs-2';
import { 
  Brain, TrendingDown, Timer, Heart, Activity, 
  Zap, Target, TrendingUp 
} from 'lucide-react';

interface ActivityReportProps {
  initialHeartRate: number;
  duration: number;
  activityType: string;
  strain: number;
  onClose: () => void;
}

export function ActivityReport({ 
  initialHeartRate, 
  duration, 
  activityType,
  strain,
  onClose 
}: ActivityReportProps) {
  const [isProcessing, setIsProcessing] = useState(true);
  const [heartRateData, setHeartRateData] = useState<number[]>([]);
  const [heartRateZones, setHeartRateZones] = useState<number[]>([]);
  const [peakHeartRate, setPeakHeartRate] = useState(0);
  const [avgHeartRate, setAvgHeartRate] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    // Simulate data processing with progress updates
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsProcessing(false);
          return 100;
        }
        return prev + 2;
      });
    }, 140); // 7 seconds total

    // Generate simulated heart rate data
    const hrData = Array.from({ length: 60 }, (_, i) => {
      const baseline = initialHeartRate;
      const phase = (i / 60) * Math.PI * 2;
      const trend = Math.sin(phase) * 20;
      const noise = Math.random() * 5;
      return Math.round(baseline + trend + noise);
    });

    setHeartRateData(hrData);
    setPeakHeartRate(Math.max(...hrData));
    setAvgHeartRate(Math.round(hrData.reduce((a, b) => a + b) / hrData.length));

    // Calculate time spent in each heart rate zone
    const maxHR = 220 - 30; // Assuming age 30 for demo
    const zones = [
      hrData.filter(hr => hr < maxHR * 0.6).length,
      hrData.filter(hr => hr >= maxHR * 0.6 && hr < maxHR * 0.7).length,
      hrData.filter(hr => hr >= maxHR * 0.7 && hr < maxHR * 0.8).length,
      hrData.filter(hr => hr >= maxHR * 0.8 && hr < maxHR * 0.9).length,
      hrData.filter(hr => hr >= maxHR * 0.9).length,
    ];
    setHeartRateZones(zones);

    return () => clearInterval(interval);
  }, [initialHeartRate]);

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false
      }
    },
    scales: {
      x: {
        grid: {
          color: 'rgba(255, 255, 255, 0.1)'
        }
      },
      y: {
        grid: {
          color: 'rgba(255, 255, 255, 0.1)'
        }
      }
    }
  };

  if (isProcessing) {
    return (
      <div className="fixed inset-0 bg-background/95 backdrop-blur-xl z-[100] flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center space-y-4"
        >
          <div className="relative">
            <Activity className="w-12 h-12 text-primary animate-pulse" />
            <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full" />
          </div>
          <h2 className="text-xl font-semibold">Processing Activity Data</h2>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-muted-foreground justify-center">
              <Brain className="w-5 h-5" />
              <span>Analyzing your {activityType.toLowerCase()}...</span>
            </div>
            <div className="text-sm text-muted-foreground">
              {progress < 30 && "Analyzing heart rate patterns..."}
              {progress >= 30 && progress < 60 && "Calculating strain metrics..."}
              {progress >= 60 && progress < 90 && "Comparing with previous activities..."}
              {progress >= 90 && "Generating insights..."}
            </div>
          </div>
          <div className="w-48 h-1 bg-muted rounded-full mx-auto overflow-hidden">
            <motion.div
              className="h-full bg-primary"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="text-sm text-muted-foreground">
            {progress}% complete
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-background/95 backdrop-blur-xl z-[100] overflow-y-auto mindful-scrollbar">
      <div className="min-h-screen p-6">
        <div className="max-w-4xl mx-auto space-y-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center space-y-2"
          >
            <h2 className="text-2xl font-semibold">Activity Report</h2>
            <p className="text-muted-foreground">{activityType}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
          >
            {/* Duration */}
            <div className="theme-card p-6">
              <div className="flex items-center gap-3 mb-4">
                <Timer className="w-5 h-5 text-primary" />
                <h3 className="font-medium">Duration</h3>
              </div>
              <div className="text-3xl font-bold">{duration} min</div>
            </div>

            {/* Average Heart Rate */}
            <div className="theme-card p-6">
              <div className="flex items-center gap-3 mb-4">
                <Heart className="w-5 h-5 text-primary" />
                <h3 className="font-medium">Avg HR</h3>
              </div>
              <div className="text-3xl font-bold">{avgHeartRate}</div>
              <div className="text-sm text-muted-foreground mt-2">bpm</div>
            </div>

            {/* Strain */}
            <div className="theme-card p-6">
              <div className="flex items-center gap-3 mb-4">
                <Target className="w-5 h-5 text-success" />
                <h3 className="font-medium">Strain</h3>
              </div>
              <div className="text-3xl font-bold">{strain.toFixed(1)}</div>
              <div className="text-sm text-muted-foreground mt-2">points</div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            {/* Heart Rate Chart */}
            <div className="theme-card p-6">
              <h3 className="font-medium mb-4">Heart Rate</h3>
              <div className="h-64">
                <Line
                  data={{
                    labels: Array.from({ length: heartRateData.length }, (_, i) => i),
                    datasets: [{
                      data: heartRateData,
                      borderColor: 'hsl(var(--primary))',
                      backgroundColor: 'hsla(var(--primary), 0.1)',
                      fill: true,
                      tension: 0.4
                    }]
                  }}
                  options={chartOptions}
                />
              </div>
            </div>

            {/* Heart Rate Zones */}
            <div className="theme-card p-6">
              <h3 className="font-medium mb-4">Heart Rate Zones</h3>
              <div className="space-y-4">
                {['Easy', 'Fat Burn', 'Cardio', 'Peak', 'Maximum'].map((zone, i) => (
                  <div key={zone} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span>{zone}</span>
                      <span>{Math.round((heartRateZones[i] / 60) * 100)}%</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary transition-all"
                        style={{ width: `${(heartRateZones[i] / 60) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="flex justify-center"
          >
            <button
              onClick={onClose}
              className="theme-button px-6 py-3 rounded-lg"
            >
              Close Report
            </button>
          </motion.div>
        </div>
      </div>
    </div>
  );
}